import { useEffect, useState, useMemo } from 'react'
import { cn } from '@/lib/cn'
import type { TocItem } from '../types'

interface DocTocProps {
  items: TocItem[]
  className?: string
}

/**
 * In-page table of contents for h2/h3 headings.
 * Highlights the active heading as the user scrolls using IntersectionObserver.
 * Rendered only when there are 2+ items — a 1-item TOC is noise.
 */
export function DocToc({ items, className }: DocTocProps) {
  const ids = useMemo(() => items.map((i) => i.id), [items])
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    if (ids.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
            break
          }
        }
      },
      // top offset matches the sticky header height (top-24 = 96px) so the
      // active heading does not flip a section early while scrolling.
      { rootMargin: '-96px 0px -60% 0px', threshold: 0 },
    )

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)

    for (const el of elements) {
      observer.observe(el)
    }

    return () => observer.disconnect()
  }, [ids])

  if (items.length < 2) return null

  return (
    <nav
      aria-label="On this page"
      className={cn('hidden xl:block xl:w-56 xl:shrink-0', className)}
    >
      <div className="sticky top-24">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-fg">
          On this page
        </p>
        <ul className="flex flex-col gap-0.5">
          {items.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={cn(
                  'block rounded py-0.5 text-sm leading-snug transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:rounded',
                  item.level === 3 && 'pl-4',
                  item.level === 2 && 'pl-1',
                  activeId === item.id
                    ? 'font-medium text-[color:var(--brand-primary,hsl(var(--primary)))]'
                    : 'text-muted-fg hover:text-fg',
                )}
              >
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  )
}
